/**
 * Pipe detection orchestrator. Public entry point — the analog of
 * `computeStockpileVolume`. Given a seed click on a Potree point cloud, it
 * recovers a straight cylinder (axis, radius, extent) robustly even when only
 * a partial arc of the pipe is visible (buried / occluded scans).
 *
 * Pipeline (see `docs/superpowers/specs/2026-05-25-pipe-detection-design.md`):
 *   1. SEED   — load a small fine-LOD box around the click.
 *   2. FIT    — PCA normals → axis from normal-covariance → Taubin circle for
 *               radius + centre. (Direction-first; radius is the fragile term.)
 *   3. GROW   — load a coarse-LOD corridor along the axis; classify on-surface
 *               inliers; grow the contiguous supported run from the seed.
 *   4. REFIT  — clutter-free IRLS cross-section fit over the run for the final
 *               radius + arc coverage + RMS, then derive endpoints.
 *
 * Heavy `[pipe]` debug logging throughout — paste the console trace back for
 * diagnosis. Toggle with `window.__PIPE_DEBUG__ = false`.
 */

import type { PointCloudOctree } from '@pnext/three-loader';
import { collectBox, collectCorridor, PointCloudWalkAbortError } from './collectRegion';
import { dbg, dbgWarn, Stopwatch } from './debug';
import { axialCoord, fitCrossSection, radialDistance } from './fitCylinder';
import { growPipe } from './growPipe';
import { segmentSeedRegion } from './seedRegion';
import { GridIndex } from './spatialIndex';
import { add, addOuter, dot, eigenSym3, normalize, scale, zeroSym3 } from './linalg';
import { DEFAULT_PIPE_OPTIONS, type PipeDetectOptions, type PipeResult, type Vec3 } from './types';

export class PipeFitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PipeFitError';
  }
}
export { PointCloudWalkAbortError as PipeDetectAbortError };

/** Minimum seed-box points to attempt a fit. */
const MIN_SEED_POINTS = 30;

/** Estimate characteristic point spacing by sampling nearest-neighbour gaps. */
function estimateSpacing(positions: Float64Array, count: number, index: GridIndex, probe: number): number {
  const out: number[] = [];
  const dists: number[] = [];
  const sampleStride = Math.max(1, Math.floor(count / 400));
  for (let i = 0; i < count; i += sampleStride) {
    const x = positions[3 * i];
    const y = positions[3 * i + 1];
    const z = positions[3 * i + 2];
    const k = index.queryRadius(x, y, z, probe, out);
    let best = Infinity;
    for (let n = 0; n < k; n++) {
      const j = out[n];
      if (j === i) continue;
      const dx = positions[3 * j] - x;
      const dy = positions[3 * j + 1] - y;
      const dz = positions[3 * j + 2] - z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d < best) best = d;
    }
    if (Number.isFinite(best)) dists.push(best);
  }
  if (dists.length === 0) return probe * 0.25;
  dists.sort((a, b) => a - b);
  return dists[Math.floor(dists.length / 2)]; // median NN distance
}

/**
 * Endpoints from the axial density profile of the (sorted) inlier coords.
 * Bins finely, takes the core (median nonzero-bin) density, and grows a
 * contiguous run around the seed while density ≥ half the core. Returns the
 * actual data extremes inside that run, so a sparse noise halo past the pipe
 * end (which falls below the cliff) is excluded regardless of its point count.
 */
function extentByDensity(sortedTs: number[], seedTRef: number, binW: number): [number, number] {
  const n = sortedTs.length;
  if (n === 0) return [0, 0];
  const minv = sortedTs[0];
  const maxv = sortedTs[n - 1];
  if (maxv - minv < binW || binW <= 0) return [minv, maxv];
  const nb = Math.max(1, Math.ceil((maxv - minv) / binW) + 1);
  const cnt = new Int32Array(nb);
  for (let i = 0; i < n; i++) cnt[Math.min(nb - 1, Math.floor((sortedTs[i] - minv) / binW))]++;

  // Core density from the PLATEAU (p85), and cut at half of that. Real pipe is
  // a dense plateau; a noise/occlusion end is often a gradual ramp, not a clean
  // cliff, so a median- or p75-based threshold lands inside the ramp and leaves
  // a tail. Anchoring on the plateau density excludes the whole ramp while a
  // uniform-density pipe (plateau ≈ everywhere) is untouched.
  const nz: number[] = [];
  for (let b = 0; b < nb; b++) if (cnt[b] > 0) nz.push(cnt[b]);
  nz.sort((a, b) => a - b);
  const core = nz.length > 0 ? nz[Math.floor(0.85 * (nz.length - 1))] : 1;
  const thresh = Math.max(1, 0.5 * core);

  let sb = Math.max(0, Math.min(nb - 1, Math.floor((seedTRef - minv) / binW)));
  if (cnt[sb] < thresh) {
    let best = -1;
    let bestD = Infinity;
    for (let b = 0; b < nb; b++) {
      if (cnt[b] >= thresh && Math.abs(b - sb) < bestD) {
        bestD = Math.abs(b - sb);
        best = b;
      }
    }
    sb = best >= 0 ? best : sb;
  }
  let lo = sb;
  let hi = sb;
  while (lo - 1 >= 0 && cnt[lo - 1] >= thresh) lo--;
  while (hi + 1 < nb && cnt[hi + 1] >= thresh) hi++;

  const loT = minv + lo * binW;
  const hiT = minv + (hi + 1) * binW;
  let a = Infinity;
  let b = -Infinity;
  for (let i = 0; i < n; i++) {
    const t = sortedTs[i];
    if (t >= loT && t <= hiT) {
      if (t < a) a = t;
      if (t > b) b = t;
    }
  }
  dbg(
    `extent: binW=${binW.toFixed(3)} core(p75)=${core} thresh=${thresh.toFixed(1)} bins=[${lo}..${hi}]/${nb} ` +
      `t=[${a.toFixed(2)}..${b.toFixed(2)}] len=${(b - a).toFixed(2)}m (seedBin=${sb})`,
  );
  return [a, b];
}

export async function detectPipe(
  pco: PointCloudOctree,
  seed: Vec3,
  opts: PipeDetectOptions = {},
): Promise<PipeResult> {
  const o = { ...DEFAULT_PIPE_OPTIONS, ...opts };
  const { onProgress, signal } = opts;
  const sw = new Stopwatch();
  dbg('▶ detectPipe seed=', seed.map((v) => v.toFixed(3)), 'opts=', o);

  // ── Phase 1: SEED — small fine-LOD box around the click. ─────────────────
  onProgress?.('seed', 0, 1);
  const seedCap = Math.min(o.maxPoints, 80_000);
  const seedBox = await collectBox(
    pco,
    seed,
    o.seedHalfSize,
    o.seedSpacing, // fixed moderate LOD: fast, uniform box coverage, no cap thrash
    seedCap,
    (_phase, done, total) => onProgress?.('seed', done, total),
    signal,
  );
  sw.lap(`SEED load: ${seedBox.count} pts in ${o.seedHalfSize * 2}m box (capped=${seedBox.capped})`);
  if (seedBox.count < MIN_SEED_POINTS) {
    throw new PipeFitError(`Too few points at click (${seedBox.count}). Click on a denser part of the pipe.`);
  }

  // ── Phase 2: SEGMENT pipe surface → axis → Taubin circle. ────────────────
  // We do NOT fit the axis from all box points — a box around a small pipe is
  // dominated by ground/wall, which pulls the normal-covariance axis estimate
  // perpendicular to the real pipe. Region-grow the pipe surface from the
  // click first (stops at the ground edge), then fit on those points only.
  onProgress?.('fitting', 0, 1);
  // Size the normal-estimation neighbourhood to the DATA spacing, never to the
  // box. Tying it to the box made the neighbourhood (12.5cm) far larger than a
  // small pipe's radius — that wraps PCA around the whole cross-section,
  // smears the normals, and yields a perpendicular axis + inflated radius.
  const probeCell = Math.max(0.01, Math.min(0.05, o.seedHalfSize / 24));
  const probeGrid = new GridIndex(seedBox.positions, seedBox.count, probeCell);
  const spacing = estimateSpacing(seedBox.positions, seedBox.count, probeGrid, Math.max(probeCell * 4, 0.08));
  // Neighbourhood ~4× spacing: enough points for a stable PCA normal, but small
  // enough to stay well inside a small pipe's radius (sub-5cm conduit needs the
  // neighbourhood < its ~2.5cm radius, or PCA wraps the cross-section).
  const neighRadius = Math.min(0.2, Math.max(0.006, spacing * 4));
  // Build the working grid at the neighbourhood scale so radius queries hit
  // small buckets (the old box-scale cell made each query scan ~100k points).
  const seedIndex = new GridIndex(seedBox.positions, seedBox.count, neighRadius);
  dbg(`spacing≈${spacing.toFixed(4)}m neighRadius=${neighRadius.toFixed(3)}m boxPts=${seedBox.count}`);

  const region = segmentSeedRegion(seedBox.positions, seedBox.count, seedIndex, seed, neighRadius);
  if (!region || region.indices.length < 50) {
    throw new PipeFitError(
      `Could not isolate a pipe surface at the click (region=${region?.indices.length ?? 0}). Click more centrally on the pipe.`,
    );
  }
  let axis0 = region.axis;
  let axisConfidence = region.axisConfidence;
  sw.lap(
    `SEGMENT: region=${region.indices.length} pts axis=${axis0.map((v) => v.toFixed(3))} axisConf=${axisConfidence.toFixed(3)}`,
  );

  // Projection origin = the surface point nearest the click.
  const refPoint: Vec3 = [
    seedBox.positions[3 * region.seedIdx],
    seedBox.positions[3 * region.seedIdx + 1],
    seedBox.positions[3 * region.seedIdx + 2],
  ];
  const seedFit = fitCrossSection(seedBox.positions, region.indices, seedBox.count, axis0, refPoint);
  sw.lap(`FIT: r0=${seedFit.radius.toFixed(3)}m arc=${seedFit.arcCoverageDeg}° rms=${seedFit.rmsM.toFixed(4)}m ok=${seedFit.ok}`);
  if (!seedFit.ok || !(seedFit.radius > 0) || seedFit.radius > o.seedHalfSize * 4) {
    throw new PipeFitError('No cylinder found at click. Try clicking more centrally on a clean section of pipe.');
  }
  const r0 = seedFit.radius;

  // ── Phase 3: GROW — coarse corridor along axis, contiguous extent. ───────
  onProgress?.('growing', 0, 1);
  const band = o.bandFrac * r0 + o.bandFloor;
  const outerRadius = r0 + Math.max(band * 2, r0 * 1.5); // generous tube to catch the whole pipe + margin
  const corridorSpacing = Math.max(r0 / 3, spacing); // coarse: just need extent, not density
  const corridorCap = Math.max(1000, o.maxPoints - seedBox.count);
  dbg(`corridor: halfLen=${o.maxSearchLength}m outerR=${outerRadius.toFixed(3)}m targetSpacing=${corridorSpacing.toFixed(3)}m cap=${corridorCap}`);

  const tGrowStart = performance.now();
  const corridor = await collectCorridor(
    pco,
    seedFit.center,
    axis0,
    o.maxSearchLength,
    outerRadius,
    corridorSpacing,
    corridorCap,
    (_phase, done, total) => onProgress?.('growing', done, total),
    signal,
  );
  const growLoadMs = performance.now() - tGrowStart;
  sw.lap(`GROW load: ${corridor.count} pts (capped=${corridor.capped}) in ${growLoadMs.toFixed(0)}ms`);
  if (growLoadMs > o.growBudgetMs) {
    dbgWarn(`grow load (${growLoadMs.toFixed(0)}ms) exceeded budget (${o.growBudgetMs}ms) — consider coarser LOD / shorter search`);
  }

  const seedT = axialCoord(seed, seedFit.center, axis0);
  const grow = growPipe(corridor.positions, corridor.count, {
    axis: axis0,
    axisPoint: seedFit.center,
    radius: r0,
    band,
    seedT,
  });
  dbg(`grow: bandInliers=${grow.totalBandInliers} runInliers=${grow.inliers.length} tStart=${grow.tStart.toFixed(2)} tEnd=${grow.tEnd.toFixed(2)} len=${(grow.tEnd - grow.tStart).toFixed(2)}m`);
  if (grow.inliers.length < MIN_SEED_POINTS) {
    throw new PipeFitError('Pipe surface too sparse to trace a length. Try a denser scan or a different click.');
  }

  // ── Axis refinement from the run's geometry. The seed-patch normal estimate
  // is a short baseline (and was low-confidence here); on a pipe longer than it
  // is wide, the largest-variance direction of the run's surface points IS the
  // axis — a far longer, more accurate baseline. Guard on elongation so a short
  // stub (where a circumferential direction could dominate) keeps the normal
  // estimate.
  {
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (const i of grow.inliers) {
      cx += corridor.positions[3 * i];
      cy += corridor.positions[3 * i + 1];
      cz += corridor.positions[3 * i + 2];
    }
    const m = grow.inliers.length;
    cx /= m;
    cy /= m;
    cz /= m;
    const cov = zeroSym3();
    for (const i of grow.inliers) {
      addOuter(cov, [corridor.positions[3 * i] - cx, corridor.positions[3 * i + 1] - cy, corridor.positions[3 * i + 2] - cz]);
    }
    const e = eigenSym3(cov);
    const cand = normalize(e.vectors[2]); // largest-variance direction
    // Elongated when axial variance clearly dominates the cross-section (ring)
    // variance. e.values ascending → [radial≈0, ring, axial].
    if (e.values[2] > 4 * e.values[1]) {
      const aligned: Vec3 = dot(cand, axis0) < 0 ? [-cand[0], -cand[1], -cand[2]] : cand;
      const deltaDeg = (Math.acos(Math.min(1, Math.abs(dot(cand, axis0)))) * 180) / Math.PI;
      axis0 = aligned;
      axisConfidence = Math.max(axisConfidence, 0.8); // long-baseline fit is reliable
      dbg(`axis refined via run-PCA: Δ=${deltaDeg.toFixed(2)}° eigratio=${(e.values[2] / (e.values[1] + 1e-9)).toFixed(1)} → axis=${axis0.map((v) => v.toFixed(3))}`);
    } else {
      dbg(`axis refine skipped (not elongated: eigratio=${(e.values[2] / (e.values[1] + 1e-9)).toFixed(1)})`);
    }
  }

  // ── Phase 4: REFIT — clutter-free IRLS cross-section over the run. ───────
  let center: Vec3 = seedFit.center;
  let radius = r0;
  let rmsM = seedFit.rmsM;
  let arcCoverageDeg = seedFit.arcCoverageDeg;
  let inliers = grow.inliers;
  for (let iter = 0; iter < 2; iter++) {
    const fit = fitCrossSection(corridor.positions, inliers, corridor.count, axis0, center);
    if (!fit.ok) break;
    center = fit.center;
    radius = fit.radius;
    rmsM = fit.rmsM;
    arcCoverageDeg = fit.arcCoverageDeg;
    const bandI = o.bandFrac * radius + o.bandFloor;
    const next: number[] = [];
    for (const i of inliers) {
      const p: Vec3 = [corridor.positions[3 * i], corridor.positions[3 * i + 1], corridor.positions[3 * i + 2]];
      if (Math.abs(radialDistance(p, center, axis0) - radius) <= bandI) next.push(i);
    }
    dbg(`refit iter${iter}: r=${radius.toFixed(3)}m arc=${arcCoverageDeg}° rms=${rmsM.toFixed(4)}m inliers ${inliers.length}→${next.length}`);
    inliers = next.length >= MIN_SEED_POINTS ? next : inliers;
  }

  // Endpoints by DENSITY CLIFF about the refined centre. A percentile trim
  // can't kill a sparse noise halo just past the pipe (too few points to cross
  // the percentile), so instead we bin the inliers' axial coords finely, find
  // the run's core (median) density, and walk out from the seed only while
  // density stays above half the core. Endpoints are the actual data extremes
  // inside that dense region — the halo, being below the cliff, is excluded.
  const axialTs = inliers
    .map((i) => axialCoord([corridor.positions[3 * i], corridor.positions[3 * i + 1], corridor.positions[3 * i + 2]], center, axis0))
    .sort((a, b) => a - b);
  const seedTRef = axialCoord(seed, center, axis0);
  const [tLo, tHi] = extentByDensity(axialTs, seedTRef, Math.min(0.15, Math.max(0.05, radius)));
  const axisU = normalize(axis0);
  const pStart: Vec3 = add(center, scale(axisU, tLo));
  const pEnd: Vec3 = add(center, scale(axisU, tHi));
  const lengthM = tHi - tLo;

  // ── Confidence: blend axis conditioning, arc coverage, radial RMS. ───────
  const arcTerm = Math.max(0, Math.min(1, arcCoverageDeg / 180));
  const rmsTerm = Math.max(0, Math.min(1, 1 - rmsM / (0.15 * radius + 1e-6)));
  const confidence = Math.max(0, Math.min(1, 0.4 * axisConfidence + 0.4 * arcTerm + 0.2 * rmsTerm));

  const result: PipeResult = {
    axis: axisU,
    center,
    radius,
    pStart,
    pEnd,
    lengthM,
    arcCoverageDeg,
    rmsM,
    inlierCount: inliers.length,
    axisConfidence,
    confidence,
    pointsConsidered: seedBox.count + corridor.count,
    durationMs: sw.total(),
  };
  dbg('✔ detectPipe result', {
    radius: +radius.toFixed(4),
    diameter: +(radius * 2).toFixed(4),
    lengthM: +lengthM.toFixed(3),
    arcCoverageDeg,
    rmsM: +rmsM.toFixed(4),
    inlierCount: inliers.length,
    confidence: +confidence.toFixed(3),
    durationMs: +result.durationMs.toFixed(0),
  });
  return result;
}
